/**
 * Schéma ou photo dans la vidéo : l'image entière d'abord, puis, quand la voix nomme une zone, la caméra s'en approche,
 * son repère s'allume et son explication apparaît ; les zones déjà nommées gardent leur numéro. Au début de chaque
 * étape, l'image revient en entier.
 */
import { Img, staticFile } from "remotion";
import { FPS, type ImageMarker, type ModelState } from "@jange/shared";
import type { Theme } from "../styles.js";

const FOCUS = Math.round(1.1 * FPS);
const ZOOM = 1.7;
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

type Pose = { cx: number; cy: number; z: number };
const FULL: Pose = { cx: 0.5, cy: 0.5, z: 1 };

/** Cadre à une image : on enchaîne les zones nommées depuis le début de l'étape (image entière au départ). */
function poseAt(state: ModelState, markers: ImageMarker[], stepFrom: number, frame: number, fx: number, fy: number): Pose {
  let prev = FULL, pose = FULL;
  for (const ev of state.history) {
    if (ev.at < stepFrom || ev.at > frame) continue;
    const m = markers.find((x) => x.id === ev.hotspotId);
    if (!m) continue;
    // Centre visé, borné pour que l'image zoomée couvre tout le cadre (fx, fy : demi-cadre en largeurs d'image).
    const bound = (v: number, half: number) => (half >= 0.5 ? 0.5 : Math.max(half, Math.min(1 - half, v)));
    const target: Pose = { cx: bound(m.x, fx / ZOOM), cy: bound(m.y, fy / ZOOM), z: ZOOM };
    const k = easeInOut(clamp01((frame - ev.at) / FOCUS));
    pose = { cx: prev.cx + (target.cx - prev.cx) * k, cy: prev.cy + (target.cy - prev.cy) * k, z: prev.z + (target.z - prev.z) * k };
    prev = target;
  }
  return pose;
}

export function Picture({ src, aspect, markers, state, stepFrom, frame, width, height, lang, theme, credit }: { src: string | null; aspect: number; markers: ImageMarker[]; state: ModelState; stepFrom: number; frame: number; width: number; height: number; lang: string; theme: Theme; credit?: string | null }) {
  const appear = state.shownAt === null ? 0 : easeOut(clamp01((frame - state.shownAt) / (0.5 * FPS)));
  // Rectangle de l'image dans le cadre (contenue, centrée).
  const w = Math.min(width, height * aspect), h = w / aspect;
  const left = (width - w) / 2, top = (height - h) / 2;
  const pose = poseAt(state, markers, stepFrom, frame, width / 2 / w, height / 2 / h);
  // Point de l'image → position à l'écran avec le zoom courant.
  const at = (x: number, y: number) => ({ x: left + w / 2 + (x - pose.cx) * w * pose.z, y: top + h / 2 + (y - pose.cy) * h * pose.z });
  const focus = state.focus && state.focus.at >= stepFrom ? state.focus : null;
  const named = new Set(state.named.map((n) => n.hotspotId));
  const label = (m: ImageMarker) => m.label[lang] ?? m.label.fr ?? m.id;
  const onWhite = !theme.rough;

  return (
    <div style={{ position: "relative", width, height, overflow: "hidden", borderRadius: theme.rough ? 0 : 22, background: onWhite ? "#FFFFFF" : "rgba(0,0,0,0.18)", opacity: appear }}>
      {src && (
        <div style={{ position: "absolute", left, top, width: w, height: h, transform: `translate(${(0.5 - pose.cx) * w * pose.z}px, ${(0.5 - pose.cy) * h * pose.z}px) scale(${pose.z})`, transformOrigin: "50% 50%" }}>
          <Img src={/^(https?:|blob:|data:)/.test(src) ? src : staticFile(src)} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
      )}
      {markers.map((m, i) => {
        if (!named.has(m.id)) return null;
        const p = at(m.x, m.y);
        const isFocus = focus?.hotspotId === m.id;
        const k = isFocus ? easeOut(clamp01((frame - focus!.at - FOCUS * 0.6) / (0.4 * FPS))) : 1;
        const right = p.x < width * 0.6;
        return (
          <div key={m.id}>
            <div style={{ position: "absolute", left: p.x - 19, top: p.y - 19, width: 38, height: 38, borderRadius: 999, border: "3px solid #fff", background: isFocus ? theme.amber : theme.accent, color: "#0F1A33", fontFamily: theme.fontFamily, fontWeight: 800, fontSize: 20, display: "grid", placeItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.35)" }}>{i + 1}</div>
            {isFocus && (
              <div style={{ position: "absolute", top: Math.max(10, Math.min(height - 170, p.y - 40)), ...(right ? { left: Math.min(width - 440, p.x + 32) } : { right: width - p.x + 32 }), maxWidth: 420, opacity: k, transform: `translateY(${(1 - k) * 8}px)`, background: "rgba(8,12,24,0.88)", color: "#fff", borderRadius: 16, padding: "12px 16px", borderLeft: `5px solid ${theme.amber}`, fontFamily: theme.fontFamily }}>
                <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1 }}>{label(m)}</div>
                {(m.text[lang] ?? m.text.fr) && <div style={{ fontSize: 22, lineHeight: 1.35, marginTop: 6, color: "#DDE3EE" }}>{m.text[lang] ?? m.text.fr}</div>}
              </div>
            )}
          </div>
        );
      })}
      {credit && <div style={{ position: "absolute", right: 12, bottom: 8, fontSize: 16, color: onWhite ? "#5B6478" : theme.muted, fontFamily: theme.fontFamily }}>{credit}</div>}
    </div>
  );
}
